import { Component, inject, signal, computed, effect } from '@angular/core';
import { Router, RouterOutlet, RouterLink, RouterLinkActive, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs/operators';
import { AuthService } from './core/auth/auth.service';
import { TenantService } from './core/services/tenant.service';
import { GoodVibesService } from './gql/good-vibes.service';

interface NavItem {
  label: string;
  icon: string;
  route: string;
  badge?: string;
}

const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', icon: 'grid', route: '/dashboard' },
      { label: 'Communications', icon: 'message-square', route: '/communications' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Orders', icon: 'clipboard-list', route: '/orders' },
      { label: 'Catalog', icon: 'layers', route: '/catalog' },
      { label: 'Subscriptions', icon: 'repeat', route: '/subscriptions' },
    ],
  },
  {
    label: 'Marketing',
    items: [
      { label: 'Promotions', icon: 'tag', route: '/promotions' },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { label: 'Regions & Tax', icon: 'map', route: '/config' },
      { label: 'Availabilities', icon: 'calendar', route: '/availabilities' },
      { label: 'Accounts', icon: 'users', route: '/accounts' },
    ],
  },
];

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive],
  template: `
    <div class="flex h-screen overflow-hidden" style="background: var(--om-bg-app)">
      <!-- Mobile overlay -->
      @if (sidebarOpen()) {
        <div
          class="fixed inset-0 z-20 lg:hidden"
          style="background: rgba(28,22,14,0.4)"
          (click)="sidebarOpen.set(false)"
        ></div>
      }

      <!-- Sidebar -->
      <aside
        class="fixed inset-y-0 left-0 z-30 flex flex-col flex-shrink-0 transition-transform duration-200 lg:translate-x-0 lg:static lg:z-auto"
        style="width: var(--om-sidebar-w); background: var(--om-bg-app); border-right: 1px solid var(--om-border-subtle)"
        [class.-translate-x-full]="!sidebarOpen()"
      >
        <!-- Brand -->
        <div class="flex items-center gap-2 px-4 py-3" style="border-bottom: 1px solid var(--om-border-subtle)">
          <div class="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
               style="background: var(--om-gray-950)">
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="white" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
          </div>
          <span class="font-display font-semibold tracking-tight truncate"
                style="font-size: 14px; color: var(--om-fg-1); letter-spacing: -0.01em">
            Omnita Operator
          </span>
        </div>

        <!-- Tenant Switcher -->
        <div class="px-3 py-2.5" style="border-bottom: 1px solid var(--om-border-subtle)">
          <div class="relative">
            <select
              class="w-full appearance-none cursor-pointer focus:outline-none"
              style="font-family: var(--om-font-display); font-size: 13px; font-weight: 500; color: var(--om-fg-1); background: var(--om-bg-surface); border: 1px solid var(--om-border-default); border-radius: 8px; padding: 5px 28px 5px 10px;"
              [value]="tenantService.activeTenantId()"
              (change)="onTenantChange($event)"
            >
              @for (t of tenantService.tenants(); track t.id) {
                <option [value]="t.id">{{ t.name }}</option>
              }
              @if (!tenantService.tenants().length) {
                <option value="">No tenants</option>
              }
            </select>
            <svg class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
                 fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
                 style="color: var(--om-fg-3)">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
            </svg>
          </div>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 overflow-y-auto px-2.5 py-3" style="display: flex; flex-direction: column; gap: 10px">
          @for (group of navGroups; track group.label) {
            <div style="display: flex; flex-direction: column; gap: 1px">
              <p class="px-2 mb-0.5"
                 style="font-family: var(--om-font-display); font-size: 12px; font-weight: 500; color: var(--om-fg-3); letter-spacing: 0; padding-top: 6px; padding-bottom: 4px">
                {{ group.label }}
              </p>
              @for (item of group.items; track item.route) {
                <a
                  [routerLink]="item.route"
                  routerLinkActive="om-nav-active"
                  [routerLinkActiveOptions]="{ exact: false }"
                  class="om-nav-item flex items-center gap-2.5 transition-default"
                  (click)="sidebarOpen.set(false)"
                >
                  <span class="w-4 h-4 flex-shrink-0" [innerHTML]="getIcon(item.icon)"></span>
                  <span>{{ item.label }}</span>
                </a>
              }
            </div>
          }
        </nav>

        <!-- User section -->
        <div class="p-2.5" style="border-top: 1px solid var(--om-border-subtle)">
          <button
            (click)="logout()"
            class="om-nav-item flex items-center gap-2.5 w-full transition-default"
            style="color: var(--om-fg-3)"
          >
            <svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
            </svg>
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      <!-- Main content -->
      <div class="flex-1 flex flex-col min-w-0 overflow-hidden">
        <!-- Topbar -->
        <header class="flex items-center gap-3 px-6 flex-shrink-0"
                style="height: var(--om-header-h); background: rgba(250,249,247,0.88); backdrop-filter: blur(12px); border-bottom: 1px solid var(--om-border-subtle)">
          <button
            class="lg:hidden p-1.5 rounded-lg transition-default"
            style="color: var(--om-fg-3)"
            (click)="sidebarOpen.set(!sidebarOpen())"
            aria-label="Toggle sidebar"
          >
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16"/>
            </svg>
          </button>

          <div class="flex-1 min-w-0">
            <h1 class="font-display font-semibold truncate"
                style="font-size: 13.5px; color: var(--om-fg-1); letter-spacing: -0.005em">
              {{ currentPageTitle() }}
            </h1>
          </div>

          <div class="flex items-center gap-2">
            @if (tenantService.activeTenant(); as tenant) {
              <span class="hidden sm:flex items-center gap-1.5"
                    style="font-size: 12px; color: var(--om-fg-2); background: var(--om-bg-surface); border: 1px solid var(--om-border-default); border-radius: 999px; padding: 3px 10px; font-family: var(--om-font-display); font-weight: 500">
                <span class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style="background: var(--om-success-fg)"></span>
                {{ tenant.name }}
              </span>
            }
            <div class="flex items-center justify-center rounded-full"
                 style="width: 28px; height: 28px; background: linear-gradient(135deg, var(--om-gray-500), var(--om-gray-700))">
              <span style="font-family: var(--om-font-display); font-size: 11px; font-weight: 600; color: #fff">
                {{ userInitial() }}
              </span>
            </div>
          </div>
        </header>

        <!-- Page content -->
        <main class="flex-1 overflow-y-auto">
          <router-outlet></router-outlet>
        </main>
      </div>
    </div>
  `,
  styles: [`
    .om-nav-item {
      display: flex;
      align-items: center;
      gap: 10px;
      height: 30px;
      padding: 0 8px;
      border-radius: 7px;
      font-family: var(--om-font-display);
      font-size: 13.5px;
      font-weight: 400;
      color: var(--om-fg-2);
      cursor: pointer;
      text-decoration: none;
      letter-spacing: -0.005em;
      white-space: nowrap;
    }
    .om-nav-item:hover {
      background: var(--om-bg-hover);
      color: var(--om-fg-1);
    }
    .om-nav-active {
      background: var(--om-bg-selected) !important;
      color: var(--om-accent-700) !important;
      font-weight: 600 !important;
    }
  `],
})
export class AppComponent {
  protected authService = inject(AuthService);
  protected tenantService = inject(TenantService);
  private goodVibesService = inject(GoodVibesService);
  private router = inject(Router);

  readonly navGroups = NAV_GROUPS;
  readonly sidebarOpen = signal(false);

  readonly userInitial = computed(() => {
    const user = this.authService.currentUser();
    return (user?.email?.[0] ?? 'O').toUpperCase();
  });

  readonly currentPageTitle = signal('Dashboard');

  constructor() {
    // Load tenant list on init
    effect(() => {
      if (this.authService.isAuthenticated()) {
        this.loadTenants();
      }
    });

    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        const url = this.router.url;
        this.currentPageTitle.set(this.titleFromUrl(url));
      });
  }

  private loadTenants(): void {
    this.goodVibesService.getProviders().subscribe({
      next: (providers) => {
        this.tenantService.setTenants(
          providers.map((p) => ({
            id: p.id,
            name: p.public_profile?.display_name ?? p.id,
            slug: p.public_profile?.url_slug,
          }))
        );
      },
      error: (err) => {
        console.error('Failed to load tenants', err);
        void this.tenantService.setTenants([]);
      },
    });
  }

  onTenantChange(event: Event): void {
    const id = (event.target as HTMLSelectElement).value;
    void this.tenantService.switchTenant(id);
  }

  logout(): void {
    this.authService.logout();
  }

  private titleFromUrl(url: string): string {
    const map: Record<string, string> = {
      '/dashboard': 'Dashboard',
      '/orders': 'Orders',
      '/catalog': 'Catalog',
      '/promotions': 'Promotions',
      '/subscriptions': 'Subscriptions',
      '/config': 'Configuration',
      '/availabilities': 'Availabilities',
      '/accounts': 'Accounts',
      '/communications': 'Communications',
    };
    const key = Object.keys(map).find((k) => url.startsWith(k));
    return key ? map[key] : 'Omnita';
  }

  getIcon(name: string): string {
    const icons: Record<string, string> = {
      grid: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" class="w-4 h-4"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>`,
      'message-square': `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>`,
      'clipboard-list': `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/></svg>`,
      layers: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>`,
      repeat: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M17 1l4 4-4 4"/><path stroke-linecap="round" stroke-linejoin="round" d="M3 11V9a4 4 0 014-4h14M7 23l-4-4 4-4"/><path stroke-linecap="round" stroke-linejoin="round" d="M21 13v2a4 4 0 01-4 4H3"/></svg>`,
      tag: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z"/></svg>`,
      map: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/></svg>`,
      users: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/></svg>`,
      calendar: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" class="w-4 h-4"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`,
    };
    return icons[name] ?? '';
  }
}
